import { fetchServer } from "@/features/utils/fetch";
import { utils } from "@passwordless-id/webauthn";
import type { CredentialInterface, LimitCredentialInterface } from "../types";

const webauthn_register_uri = {
  urlCheckActivation: "/webauthn/register/check_activation",
  urlRegistrationOptions: "/webauthn/register/options",
  urlVerifyUserRegistration: "/webauthn/register/verify",
  urlActivateDeactivateCredential: "/webauthn/register/activate_deactivate_credential",
  urlUpdateDeviceName: "/webauthn/register/update_device_name",
  urlRetrieveWebauthnActiveCredentials: "/webauthn/register/retrieve_user_active_credentials"
};

export class WebAuthnRegisterApi {
  /**
   *
   */
  static async checkActivation() {
    const response = await fetchServer(webauthn_register_uri.urlCheckActivation, {
      method: "GET",
      mainRoot: true
    });

    if (200 === response.status) {
      const res = await response.json();
      return res.data as {
        isWebAuthnActivated: boolean;
      };
    } else {
      // Handle cases where data is empty or not valid
      throw new Error("Invalid checkActivation");
    }
  }

  /**
   *
   */
  static async getRegistrationOptions() {
    const response = await fetchServer(webauthn_register_uri.urlRegistrationOptions, {
      method: "GET",
      mainRoot: true,
      reloadOn401: false
    });

    if (200 === response.status) {
      const res = await response.json();
      return res.data as {
        options: {
          uuid: string;
          rp: {
            name: string;
            id: string;
          };
          user: PublicKeyCredentialUserEntity & { userIdentifier: string };
          challenge: string;
          pubKeyCredParams: PublicKeyCredentialParameters[];
          timeout: number;
          attestation: AttestationConveyancePreference;
          authenticatorSelection: {
            requireResidentKey: boolean;
            userVerification: UserVerificationRequirement;
          };
          excludeCredentials: {
            type: string;
            id: string;
          }[];
        };
      };
    } else {
      // Handle cases where data is empty or not valid
      throw new Error("Invalid registrationOptions");
    }
  }

  /**
   *
   * @param uuid
   * @param clientAttestationResponse
   * @param deviceName
   */
  static async verifyUserRegistration(
    uuid: string,
    clientAttestationResponse: {
      rawId: string;
      response: {
        attestationObject: string;
        clientDataJSON: string;
      };
      partialAuthenticatorInfos: {
        authenticatorAttachment: string;
        transports: string[];
        userShaId: string;
      };
    },
    deviceName: string,
    checkAuthorisation?: boolean
  ) {
    const response = await fetchServer(webauthn_register_uri.urlVerifyUserRegistration, {
      method: "POST",
      mainRoot: true,
      body: {
        clientAttestationResponse: clientAttestationResponse,
        deviceName: deviceName,
        uuid: uuid,
        checkAuthorisation
      }
    });
    if (200 === response.status) {
      const res = await response.json();
      return res.data as {
        challenge: string;
      }; // Return valid data
    } else {
      // Handle cases where data is empty or not valid
      throw new Error("Invalid verifyUserRegistration");
    }
  }
  /*
   * @param inputBase64
   */
  static decodeBase64(inputBase64: string) {
    // Replace URL-safe characters
    let base64 = inputBase64.replace(/-/g, "+").replace(/_/g, "/");
    // Add padding if needed
    while (base64.length % 4 !== 0) {
      base64 += "=";
    }
    return base64;
  }
  /**
   *
   * @param deviceName
   */
  static async registerWebAuthnUser(deviceName: string, checkAuthorisation?: boolean) {
    try {
      const response = await WebAuthnRegisterApi.getRegistrationOptions();
      if (!response.options) {
        throw new Error("Invalid registrationOptions");
      }
      const registrationOptions = response.options;
      // base 64
      const registrationOptionsChallenge = utils.toBuffer(registrationOptions.challenge);
      const registrationOptionsUserId = await utils.sha256(
        new TextEncoder().encode(`WEBAUTHN_USER_:${registrationOptions.user.name}${Date.now()}`)
          .buffer
      );
      if (deviceName === "TODO_CHANGE_ME") {
        deviceName = `Appareil de ${registrationOptions.user.userIdentifier}`;
      }
      const excludeCredentials: PublicKeyCredentialDescriptor[] =
        registrationOptions.excludeCredentials.map((credential) => ({
          type: "public-key",
          id: new Uint8Array(
            atob(WebAuthnRegisterApi.decodeBase64(credential.id))
              .split("")
              .map((char) => char.charCodeAt(0))
          )
        }));
      const registrationOptionsPayload: PublicKeyCredentialCreationOptions = {
        ...registrationOptions,
        excludeCredentials: excludeCredentials,
        challenge: registrationOptionsChallenge,
        pubKeyCredParams: registrationOptions.pubKeyCredParams,
        rp: registrationOptions.rp,
        user: { ...registrationOptions.user, id: registrationOptionsUserId },
        attestation: registrationOptions.attestation,
        authenticatorSelection: registrationOptions.authenticatorSelection,
        timeout: registrationOptions.timeout
      };
      const rawClientAttestationResponse = (await navigator.credentials.create({
        publicKey: registrationOptionsPayload
      })) as unknown as {
        rawId: ArrayBuffer;
        response: AuthenticatorAttestationResponse;
        authenticatorAttachment: string;
        type: string;
      };
      // we put as Array buffer because buffersource is an union between ArrayBufferView | ArrayBuffer
      const userShaId = utils.toBase64url(registrationOptions.user.id as ArrayBuffer);
      if (rawClientAttestationResponse) {
        const clientAttestationResponse = {
          rawId: utils.toBase64url(rawClientAttestationResponse?.rawId),
          response: {
            attestationObject: utils.toBase64url(
              rawClientAttestationResponse?.response?.attestationObject
            ),
            clientDataJSON: utils.toBase64url(
              rawClientAttestationResponse?.response?.clientDataJSON
            )
          },
          partialAuthenticatorInfos: {
            authenticatorAttachment: rawClientAttestationResponse.authenticatorAttachment,
            transports: rawClientAttestationResponse.response.getTransports(),
            userShaId: userShaId
          }
        };
        const registrationResult = await WebAuthnRegisterApi.verifyUserRegistration(
          registrationOptions.uuid,
          clientAttestationResponse,
          deviceName,
          checkAuthorisation
        );
        if (registrationResult.challenge) {
          return registrationResult.challenge;
        } else {
          return null;
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  /**
   *
   * @param id
   * @param active
   */
  static async activateDeactivateCredential(id: number, active: boolean) {
    const response = await fetchServer(webauthn_register_uri.urlActivateDeactivateCredential, {
      method: "POST",
      mainRoot: true,
      body: {
        credential_id: id,
        active
      }
    });
    if (200 === response.status) {
      const res = await response.json();
      return res.data as CredentialInterface; // Return valid data
    } else {
      // Handle cases where data is empty or not valid
      throw new Error("Invalid activateDeactivateCredential");
    }
  }

  /**
   *
   * @param id
   * @param active
   */
  static async updateDeviceName(id: number, deviceName: string) {
    const response = await fetchServer(webauthn_register_uri.urlUpdateDeviceName, {
      method: "POST",
      mainRoot: true,
      body: {
        credential_id: id,
        device_name: deviceName
      }
    });
    if (200 === response.status) {
      const res = await response.json();
      return res.data as CredentialInterface; // Return valid data
    } else {
      // Handle cases where data is empty or not valid
      throw new Error("Invalid updateDeviceName");
    }
  }
  /**
   *
   * @param email
   */
  static async retrieveWebAuthnActiveCredentials() {
    const response = await fetchServer(webauthn_register_uri.urlRetrieveWebauthnActiveCredentials, {
      method: "POST",
      mainRoot: true,
      body: {}
    });
    // consume the recaptcha token
    if (200 === response.status) {
      const res = await response.json();
      return res.data as Array<LimitCredentialInterface>;
    } else {
      // Handle cases where data is empty or not valid
      throw new Error("Invalid retrieveWebAuthnActiveCredentials");
    }
  }
}
